import React, { useState } from 'react';
import LandingNavbar from '../components/layout/LandingNavbar';
import { AnimatedShinyText } from '../components/common/AnimatedShinyText';
import { TextAnimate } from '../components/common/TextAnimate';
import { InteractiveButton } from '../components/common/InteractiveButton';
import { ShineBorder } from '../components/common/ShineBorder';
import { ShieldAlert, Zap, Skull, ShieldCheck, Crosshair, Github, Instagram, Linkedin } from 'lucide-react';
import { apiClient } from '../utils/api';
import logo from '../Logo/logo.png';
import './MonsoonCommuteTest.css';

export default function MonsoonCommuteTest({ company, onNavigate }) {
  const [formData, setFormData] = useState({
    appName: '',
    playStoreUrl: '',
    email: '',
    arena: 'vault'
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCheckbox = (arenaType) => {
    setFormData(prev => ({ ...prev, arena: arenaType }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await apiClient.submitGauntlet({
        appName: formData.appName,
        playStoreUrl: formData.playStoreUrl,
        email: formData.email,
        arena: formData.arena
      });
      setSubmitted(true);
    } catch (err) {
      console.error("Submission failed.", err);
      // We still show submitted to not block the user, or we could show an error
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mct-page">
      <div className="mct-glow" />
      <LandingNavbar company={company} onNavigate={onNavigate} view="gauntlet" />

      <main className="mct-container">
        {/* HERO SECTION */}
        <section className="mct-hero">
          <div className="mct-badge-wrapper">
            <AnimatedShinyText className="mct-badge">
              Warning: Not for fragile egos
            </AnimatedShinyText>
          </div>
          
          <h1 className="mct-title">
            <TextAnimate as="span" animation="blurInUp" by="word">
              Your Figma File Is Gorgeous.
            </TextAnimate>
            <br />
            <span className="text-muted">
              <TextAnimate as="span" animation="blurInUp" by="word">
                Your App Is 
              </TextAnimate>
            </span>
            {' '}
            <span className="text-saffron">
              <TextAnimate as="span" animation="blurInUp" by="word">
                Garbage
              </TextAnimate>
            </span>
            {' '}
            <span className="text-muted">
              <TextAnimate as="span" animation="blurInUp" by="word">
                In The Wild.
              </TextAnimate>
            </span>
          </h1>

          <div className="mct-hero-copy">
            <p>
              Stop lying to yourselves with BrowserStack simulations. You think your code works because you clicked around on a synthetic emulator in a climate-controlled office in Koramangala.
            </p>
            <p>
              Reality check: Your real customers are sweating in the back of an auto rickshaw, relying on a 3-year-old budget Android with 200MB of free RAM and a violently fluctuating Airtel 4G connection. When they try to pay you, your slick React Native checkout flow stalls, times out, and they bounce.
            </p>
            
            <div className="mct-challenge-text">
              We are here to break your app before your customers do. Submit your build. We unleash real humans on garbage networks to find out why you're bleeding revenue.
            </div>
          </div>
        </section>

        <div className="mct-grid">
          {/* THE PROTOCOL */}
          <section className="mct-protocol">
            <h2 className="mct-section-heading">How We Break It</h2>
            <div className="mct-protocol-cards">
              <div className="mct-card">
                <div className="mct-card-icon"><ShieldAlert size={28} /></div>
                <h3>Hardware Abuse</h3>
                <p>We specifically target older Redmis, Moto Gs, and entry-level phones. Shattered screens, low RAM, bloated storage, zero breathing room for your massive DOM.</p>
              </div>
              <div className="mct-card">
                <div className="mct-card-icon"><Zap size={28} /></div>
                <h3>Network Hostility</h3>
                <p>We simulate the worst of Indian telecom. Packet drops, latency spikes, and the dreaded "E" symbol dropping right in the middle of a Razorpay handshake.</p>
              </div>
              <div className="mct-card">
                <div className="mct-card-icon"><Skull size={28} /></div>
                <h3>Human Chaos</h3>
                <p>No clean automated scripts here. Impatient humans tapping furiously when your loader stalls, hitting the back button at the worst possible microsecond.</p>
              </div>
            </div>

            <div className="mct-bounty">
              <ShineBorder shineColor={["#ffffff", "rgba(255,255,255,0)"]} duration={12} opacity={0.15} />
              <h2>The Ultimatum</h2>
              <p>If your app survives the Gauntlet with zero fatal crashes and a seamless checkout experience, you get immortalized in our <strong>Hall of Fame</strong>. We'll bow to your engineering prowess publicly.</p>
              <p>When it breaks (and it will), we drag you onto a Zoom call. We hand you the crash logs, the screen recordings, and show you exactly how much money you're losing every day. The brutal truth is free.</p>
            </div>
          </section>

          {/* THE FORM */}
          <section className="mct-form-section">
            <div className="mct-form-wrapper">
              <ShineBorder shineColor={["#FF6B2B", "#FFD700"]} duration={8} opacity={0.4} />
              <h2 className="mct-form-title">Enter The Meatgrinder</h2>
              
              {submitted ? (
                <div className="mct-success-state">
                  <ShieldCheck size={64} className="text-saffron" style={{ margin: '0 auto 24px auto' }} />
                  <h3>May god have mercy on your code</h3>
                  <p>We have your details. The testers have been deployed. You'll hear from us when the dust settles.</p>
                  <InteractiveButton 
                    variant="outline" 
                    onClick={() => setSubmitted(false)}
                    style={{ marginTop: '32px' }}
                  >
                    Submit Another Victim
                  </InteractiveButton>
                </div>
              ) : (
                <form className="mct-form" onSubmit={handleSubmit}>
                  <div className="mct-input-group">
                    <label>What's the App Called?</label>
                    <input 
                      type="text" 
                      name="appName"
                      placeholder="e.g. The Next Unicorn" 
                      value={formData.appName}
                      onChange={handleChange}
                      required 
                    />
                  </div>
                  
                  <div className="mct-input-group">
                    <label>Drop The Play Store URL (or APK)</label>
                    <input 
                      type="url" 
                      name="playStoreUrl"
                      placeholder="https://..." 
                      value={formData.playStoreUrl}
                      onChange={handleChange}
                      required 
                    />
                  </div>
                  
                  <div className="mct-input-group">
                    <label>Founder / CTO Email</label>
                    <input 
                      type="email" 
                      name="email"
                      placeholder="Who gets the bad news?" 
                      value={formData.email}
                      onChange={handleChange}
                      required 
                    />
                  </div>

                  <div className="mct-arena-selector">
                    <div className="mct-arena-label">Choose Your Execution Style</div>
                    
                    <div 
                      className={`mct-arena-option ${formData.arena === 'vault' ? 'active' : ''}`}
                      onClick={() => handleCheckbox('vault')}
                    >
                      <div className="mct-arena-radio">
                        {formData.arena === 'vault' && <div className="mct-arena-radio-inner" />}
                      </div>
                      <div className="mct-arena-info">
                        <h4>The Vault</h4>
                        <span>100% Private. We rip it apart behind closed doors. Your ego survives.</span>
                      </div>
                    </div>

                    <div 
                      className={`mct-arena-option ${formData.arena === 'colosseum' ? 'active' : ''}`}
                      onClick={() => handleCheckbox('colosseum')}
                    >
                      <div className="mct-arena-radio">
                        {formData.arena === 'colosseum' && <div className="mct-arena-radio-inner" />}
                      </div>
                      <div className="mct-arena-info">
                        <h4>The Colosseum</h4>
                        <span>Public Teardown. We publish the carnage on Twitter & LinkedIn. High risk, immense glory.</span>
                      </div>
                    </div>
                  </div>

                  <InteractiveButton 
                    variant="primary" 
                    filled={true} 
                    type="submit"
                    className="mct-submit-btn"
                    disabled={submitting}
                  >
                    {submitting ? 'Preparing Devices...' : 'Bring It On'}
                  </InteractiveButton>
                  <p className="mct-form-disclaimer">By submitting, you agree that we might break your app. That's the whole point.</p>
                </form>
              )}
            </div>
          </section>
        </div>
      </main>

      {/* ── Footer ── */}
      <footer className="hp-footer">
        <div className="hp-footer-inner">
          <div className="hp-footer-col">
            <div className="hp-logo hp-logo--footer">
              <img src={logo} alt="BharatQA" className="hp-logo-img hp-logo-img--footer" />
            </div>
            <p className="hp-footer-tagline">
              Crowdsourced QA testing for Android apps.<br />Built for Indian developers.
            </p>
          </div>
          <div className="hp-footer-col">
            <h4>Office</h4>
            <address>
              Shadat Colony, Gudihal Road,<br />
              Vishal Nagar, Hubballi
            </address>
          </div>
          <div className="hp-footer-col">
            <h4>Contact</h4>
            <p><a href="tel:+916361434273">+91 6361434273</a></p>
            <p><a href="tel:+918123548635">+91 8123548635</a></p>
            <p><a href="mailto:fundup3@gmail.com">fundup3@gmail.com</a></p>
            <p><a href="mailto:zaheer@fundup7.in">zaheer@fundup7.in</a></p>
          </div>
          <div className="hp-footer-col">
            <h4>Social</h4>
            <div className="hp-footer-socials">
              <a href="https://github.com/fundup7" target="_blank" rel="noopener noreferrer"><Github size={18} /> <span>GitHub</span></a>
              <a href="https://www.instagram.com/bharatqa/" target="_blank" rel="noopener noreferrer"><Instagram size={18} /> <span>Instagram</span></a>
              <a href="https://www.linkedin.com/company/bharatqa/" target="_blank" rel="noopener noreferrer"><Linkedin size={18} /> <span>LinkedIn</span></a>
            </div>
          </div>
        </div>
        <div className="hp-footer-bottom">
          <span>© {new Date().getFullYear()} BharatQA. All rights reserved.</span>
          <div className="hp-footer-links">
            <button onClick={() => onNavigate('changelog')}>Changelog / Status</button>
            <button onClick={() => onNavigate('terms')}>Terms of Service</button>
            <button onClick={() => onNavigate('privacy')}>Privacy Policy</button>
          </div>
          <span>Made in India 🇮🇳</span>
        </div>
      </footer>
    </div>
  );
}
